"use client";

import type { TabItem } from "@kyan/ui";
import { cn, Tabs } from "@kyan/ui";
import { motion } from "motion/react";

import { OpenOrders } from "#/components/OpenOrders/OpenOrders";
import { Positions } from "#/components/Positions/Positions";
import { useUserScopes } from "#/services/dynamic/hooks/useUserScopes";
import type { PositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { useShowTradePanel } from "#/store/app/tradePanel/showTradePanel";
import { useRfqResponses } from "#/store/rfq/rfqResponses";
import { useOpenOrders } from "#/store/wallet/account/openOrders";
import { usePositions } from "#/store/wallet/account/positions";
import { appendCount } from "#/utils/appendCount";

import { TradePanelAreaLimiter } from "../TradePanel/TradePanel";
import { DragButton } from "./components/DragButton";
import { Rfq } from "./components/Rfq/Rfq";
// import { Rfq } from "./components/Rfq/Rfq";
import { RfqModal } from "./components/RfqModal/RfqModal";
import { ToggleButton } from "./components/ToggleButton";
import { useDrawerDrag } from "./PositionsDrawer.hooks";

const getTabs = ({
  positionsCount,
  openOrdersCount,
  rfqCount,
}: {
  positionsCount: number;
  openOrdersCount: number;
  rfqCount: number;
}): TabItem<PositionsDrawerTab>[] => [
  {
    id: "positions",
    label: appendCount("Positions", positionsCount),
  },
  {
    id: "open-orders",
    label: appendCount("Open Orders", openOrdersCount),
  },
  {
    id: "rfq",
    label: appendCount("RFQ", rfqCount),
  },
];

export const PositionsDrawer: React.FC = () => {
  const {
    top,
    drawerRef,
    animation,
    dragControls,
    drawerHeight,
    contentHeight,
    positionsDrawerTab,
    positionsDrawerSettings,
    handleDrag,
    handleToggle,
    handleDragEnd,
    handleChangeTab,
    handleDragUpdate,
    handleAnimationComplete,
  } = useDrawerDrag();
  const { positions } = usePositions();
  const { openOrders } = useOpenOrders();
  const { hasEarlyAccessVip } = useUserScopes();

  const { hasAnyRfqResponse, marketResponses } = useRfqResponses();
  const { showTradePanel } = useShowTradePanel();

  const tabs = getTabs({
    positionsCount: positions.length,
    openOrdersCount: openOrders.length,
    rfqCount: marketResponses.length,
  });

  const filteredTabs = hasEarlyAccessVip
    ? tabs
    : tabs.filter((tab) => tab.id !== "rfq");

  return (
    <motion.aside
      drag="y"
      dragElastic={0}
      ref={drawerRef}
      style={{ y: top }}
      animate={animation}
      dragListener={false}
      dragMomentum={false}
      dragControls={dragControls}
      initial={{ y: drawerHeight }}
      dragConstraints={{ top: 0, bottom: drawerHeight }}
      transition={{ type: "spring", stiffness: 200, damping: 25 }}
      className="bg-background pointer-events-auto fixed bottom-0 flex h-1/2 w-full flex-col"
      onDrag={handleDragUpdate}
      onDragEnd={handleDragEnd}
      onAnimationComplete={handleAnimationComplete}
    >
      <div
        className={cn(
          "bg-background border-divider absolute flex h-10 w-full -translate-y-full items-center justify-between gap-4 border-y pl-10 transition-all",
          {
            "w-[calc(100%-22rem)]": showTradePanel,
            "w-[calc(100%-3.75rem)]": !showTradePanel,
          },
        )}
      >
        <div className="absolute top-0 left-1/2 flex h-7 -translate-x-1/2 -translate-y-full items-center gap-1">
          <ToggleButton
            variant="primary"
            active={positionsDrawerSettings.show}
            onToggle={() => {
              handleToggle(false);
            }}
          />
        </div>
        <DragButton handleDrag={handleDrag} showTradePanel={showTradePanel} />
        <Tabs
          variant="primary"
          items={filteredTabs}
          value={positionsDrawerTab}
          itemClassName="h-10 text-xs whitespace-nowrap"
          unread={hasAnyRfqResponse ? ["rfq"] : undefined}
          onChange={handleChangeTab}
        />
        <div className="flex gap-3">
          {positionsDrawerTab === "rfq" && positionsDrawerSettings.show && (
            <RfqModal />
          )}
          <ToggleButton
            variant="secondary"
            active={positionsDrawerSettings.show}
            onToggle={() => {
              handleToggle(true);
            }}
          />
        </div>
      </div>

      <TradePanelAreaLimiter height={contentHeight}>
        {positionsDrawerTab === "positions" && <Positions />}
        {positionsDrawerTab === "open-orders" && <OpenOrders />}
        {positionsDrawerTab === "rfq" && hasEarlyAccessVip && <Rfq />}
      </TradePanelAreaLimiter>
    </motion.aside>
  );
};
